"use client";

import { ArrowRight, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What should I bring to my first appointment?",
    answer: "Please bring your photo ID, insurance card, a list of current medications (including inhalers), and any recent imaging or test results related to your respiratory health."
  }, {
    question: "Do I need a referral to see Dr. Qayyum?",
    answer: "While many insurance plans require a referral from your primary care physician, some do not. We recommend checking with your insurance provider or calling our office for guidance."
  }, {
    question: "How do I schedule a sleep study?",
    answer: "Sleep studies are scheduled following an initial consultation with Dr. Qayyum to determine the most appropriate type of evaluation for your symptoms."
  }, {
    question: "What is the process for prescription refills?",
    answer: "Please contact your pharmacy directly for refill requests. They will coordinate with our office to ensure your medications are managed safely and efficiently."
  }, {
    question: "How long does a typical consultation take?",
    answer: "Initial consultations usually take 45 to 60 minutes, allowing for a thorough review of your medical history and a comprehensive physical examination."
  }, {
    question: "Do you offer telehealth appointments?",
    answer: "Yes, we provide telehealth options for certain types of follow-up visits. Please inquire with our staff when scheduling to see if your visit qualifies."
  }, {
    question: "What insurance plans do you accept?",
    answer: "We accept most major insurance plans, including Medicare and Medicaid. Please contact our billing department to verify coverage for your specific plan."
  }, {
    question: "How can I access my medical records?",
    answer: "Patients can request copies of their medical records by contacting our office and completing a standard release of information form."
  }];
  return <section data-section-id="387" id="common-questions" className="relative bg-background text-foreground py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 1px 1px, var(--foreground) 1px, transparent 0)",
      backgroundSize: "24px 24px"
    }} aria-hidden="true" />

      <div className="relative container mx-auto px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-8 bg-primary/40" aria-hidden="true" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Patient FAQ
                </span>
              </div>

              <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-5xl leading-[1.05] tracking-tight text-foreground mb-6">
                Common{" "}
                <em className="font-serif italic font-light text-foreground/90">
                  questions
                </em>
                <span className="text-primary">.</span>
              </h2>

              <p className="font-default text-base leading-relaxed text-slate-text mb-10 max-w-sm">
                Clear information regarding your pulmonary care, sleep studies, and what to expect during your visit to Chaudhry Pulmonary Associates.
              </p>

              <div className="pt-8 border-t border-border">
                <p className="font-default text-sm text-muted-foreground mb-3">
                  Need further assistance?
                </p>
                <Link to="/ContactPage" className="inline-flex items-center gap-2 group text-foreground hover:text-primary transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm font-medium border-b border-foreground/30 group-hover:border-primary pb-0.5 transition-colors">
                    Contact our office
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-700 ease-out delay-100 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-slate-200 first:border-t first:border-slate-200">
                  <AccordionTrigger className="group py-6 hover:no-underline [&>svg]:hidden text-left">
                    <div className="flex items-start justify-between w-full gap-6">
                      <span className="font-serif font-default text-lg md:text-xl font-medium text-foreground leading-snug pr-2">
                        {faq.question}
                      </span>
                      <span className="relative flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center transition-transform duration-300 ease-out group-data-[state=open]:rotate-45" aria-hidden="true">
                        <span className="absolute h-px w-4 bg-foreground/70" />
                        <span className="absolute w-px h-4 bg-foreground/70" />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-12">
                    <p className="font-default text-base text-slate-text" style={{
                  lineHeight: "1.65"
                }}>
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/ContactPage" className="group inline-flex items-center gap-2 text-foreground hover:text-primary transition-colors">
                <span className="font-serif font-default text-lg italic">Contact Office</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}